/* eslint-disable no-magic-numbers */
import activityApi from '@/apis/activity'
import productApi from '@/apis/product'
import { EntranceType } from '@/types/apis/index'
import { saveFile, getSuffix } from './common-js'
import { ThirdpartyType } from '@/constants/product-enum'
import { RqResult } from '@/apis'
import { trackDelayedJump } from '@/hooks/useArmsCustomTracker'
import { trackWechatInstallApp } from '@/utils/wechat-install-sensors'
import { isWeixin } from './device'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Tree = Record<string, any>

interface Options<T> {

  /** 子节点数组属性名 */
  childrenProp?: string

  /** 父节点 */
  parent?: T | null;
}

export interface RenderProductItem {
  /** 产品id */
  id: number | null
  /**
   * 产品名称
  */
  productNickName?: null | string;
  /**
   * 产品logo
   */
  productLogo?: null | string;
  /**
   * 第三方合作方式
   */
  thirdpartyType?: number;
  /**
   * 产品发行的公司主体
  */
  productCompany: string | null
  /**
   * 全流程下游产品的公司主体
   */
  wholeProcessProductCompany?:string | null
}

interface JSONPRequestOptions {
  timeout?: number; // 超时时间，单位为毫秒，默认 5000
  callbackParam?: string; // 回调函数名称参数，默认 'callback'
  params?: Record<string, string | number>; // 传入的参数
}

// 递归树结构的数据，并对每一个节点执行 callback
export const traverseTree = <T extends Tree>(tree: T[], callback: (treeItem: T, parentItem: T | null) => void, {
  parent = null,
  childrenProp = 'children'
}: Options<T> = {}) => {
  tree.forEach(treeItem => {
    callback(treeItem, parent)
    const children = treeItem[childrenProp] as T[]
    if (children && Array.isArray(children) && children.length) {
      traverseTree(children, callback, {
        parent: treeItem,
        childrenProp
      })
    }
  })
}

// 跳转到内嵌页面
export const jumpInlinePage = async(productId: number, entranceType:EntranceType | undefined, productName?: string) => {
  const res = await productApi.apply({
    productId, //产品id
    entranceType, // 入口类型，1-首页中插，2-首页弹窗、3-开屏、4-顶部推荐、5-首页列表、6-产品列表 7-随机推荐
    appH5: 4 //有钱助手
  })

  navigateToExternal(`/inline-page?url=${encodeURIComponent(JSON.stringify(res.thirdpartyTargetUrl))}${productName ? `&productName=${productName}` : ''}`)
}

// 上报产品曝光
export const reportProductExposure = (productId: number, entranceType:EntranceType, appH5?:number,planId?:string) => {
  activityApi.reportProductExposure({
    productId,
    entranceType,
    appH5,
    planId
  })
}

// 生成随机数范围
export const randomNum = (minNum:number, maxNum:number) => {
  return parseInt((Math.random() * (maxNum - minNum + 1) + minNum).toString(), 10)
}

export { REAL_NAME_MAX_LENGTH, REAL_NAME_VALIDATION_MESSAGE, validateRealName } from './real-name'

/**
 * 通过身份证号码查出生日期
 * @param {string} idCard 身份证号码
 * @returns {string} birthday
 */
export function getBirthday(idCard:string):string {
  let birthday = ''
  if (idCard != null && idCard !== '') {
    if (idCard.length === 15) {
      birthday = `19${idCard.substring(6, 12)}`
    } else if (idCard.length === 18) {
      birthday = idCard.substring(6, 14)
    }

    birthday = birthday.replace(/(.{4})(.{2})/, '$1-$2-')
  }

  return birthday
}

/**
 * 通过身份证判断性别
 * @param {string} idCard 身份证号码
 * @returns {string} birthday
 */
export function getSex(idCard: string):string {
  let sexStr = ''
  if (parseInt(idCard.slice(-2, -1)) % 2 === 1) {
    sexStr = '1'
  } else {
    sexStr = '2'
  }
  return sexStr
}

/**
 * 通过身份证号计算年龄
 * @param {string} idCard 身份证号码
 * @returns {number} birthday
 */
export function getAge(idCard:string):number {
  let len = (`${idCard}`).length
  if (len === 0) {
    return 0
  } else if (len !== 15 && len !== 18) {
    //身份证号码只能为15位或18位其它不合法
    return 0
  }
  let strBirthday = ''
  if (len === 18) {
    //处理18位的身份证号码从号码中得到生日和性别代码
    strBirthday
      = `${idCard.substring(6, 10)
      }/${
        idCard.substring(10, 12)
      }/${
        idCard.substring(12, 14)}`
  }
  if (len === 15) {
    strBirthday
      = `19${
        idCard.substring(6, 8)
      }/${
        idCard.substring(8, 10)
      }/${
        idCard.substring(10, 12)}`
  }
  //时间字符串里，必须是“/”
  let birthDate = new Date(strBirthday)
  let nowDateTime = new Date()
  let age = nowDateTime.getFullYear() - birthDate.getFullYear()
  //再考虑月、天的因素;.getMonth()获取的是从0开始的，这里进行比较，不需要加1
  if (
    nowDateTime.getMonth() < birthDate.getMonth()
    || (nowDateTime.getMonth() === birthDate.getMonth()
      && nowDateTime.getDate() < birthDate.getDate())
  ) {
    age--
  }
  return age
}

/**
 * JsonP请求封装，
 * @param {string} url - 请求路径
 * @param {JSONPRequestOptions} options - 请求参数和配置
 * @returns {any} - 请求数据返回
 */

export const jsonpRequest = <T>(
  url: string,
  options: JSONPRequestOptions = {}
): Promise<T> => {
  const { timeout = 5000, callbackParam = 'callback', params = {} } = options

  return new Promise((resolve, reject) => {
    const callbackName = params.callback || `jsonp_callback_${Date.now()}`
    const script = document.createElement('script')

    // 将参数对象转换为字符串类型
    const queryString = new URLSearchParams(
      Object.entries({
        ...params,
        [callbackParam]: callbackName, // 添加回调函数名
      }).map(([key, value]) => [key, String(value)]) // 转换为字符串类型
    ).toString()

    // 拼接最终的请求 URL
    const jsonpUrl = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;

    // 创建一个全局回调函数，处理成功响应
    (window as any)[callbackName] = (data: T) => {
      resolve(data)
      // 请求完成后清理脚本和全局回调
      document.body.removeChild(script)
      delete (window as any)[callbackName]
    }

    // 设置超时处理
    const timeoutId = setTimeout(() => {
      reject(new Error('JSONP request timed out'))
      // 清理脚本和全局回调
      document.body.removeChild(script)
      delete (window as any)[callbackName]
    }, timeout)

    // 处理脚本加载错误
    script.onerror = () => {
      clearTimeout(timeoutId)
      reject(new Error('JSONP request failed'))
      document.body.removeChild(script)
      delete (window as any)[callbackName]
    }

    // 向 DOM 中添加脚本
    script.src = jsonpUrl
    document.body.appendChild(script)
  })
}


/**
 * 判断手机号
 * @param {string} sUserAgent - 用户使用设备信息
 * @returns {string} - 用户使用设备
 */

export const judgeBrand = (sUserAgent?: string): string => {
  if (!sUserAgent) {
    sUserAgent = navigator.userAgent
  }
  const isIphone = sUserAgent.match(/iphone/i) !== null
  const isHuawei = sUserAgent.match(/huawei/i) !== null
  const isHonor = sUserAgent.match(/honor/i) !== null
  const isOppo = sUserAgent.match(/oppo/i) !== null
  const isOppoR15 = sUserAgent.match(/pacm00/i) !== null
  const isVivo = sUserAgent.match(/vivo/i) !== null
  const isXiaomi = sUserAgent.match(/mi\s/i) !== null
  const isXiaomi2s = sUserAgent.match(/mix\s/i) !== null
  const isRedmi = sUserAgent.match(/redmi/i) !== null
  const isSamsung = sUserAgent.match(/sm-/i) !== null

  if (isIphone) {
    return 'iphone'
  } else if (isHuawei || isHonor) {
    return 'huawei'
  } else if (isOppo || isOppoR15) {
    return 'oppo'
  } else if (isVivo) {
    return 'vivo'
  } else if (isXiaomi || isRedmi || isXiaomi2s) {
    return 'xiaomi'
  } else if (isSamsung) {
    return 'samsung'
  } else {
    return 'default'
  }
}

// 重试函数
export const retry = async <T>(
  fn: () => Promise<T>,
  times = 3,
  delay = 0
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    if (times === 0) {
      // 重试次数用完，抛出最后一次的错误
      throw error
    }

    // 延迟重试
    await new Promise(resolve => setTimeout(resolve, delay))

    // 递归重试，次数减一
    return retry(fn, times - 1, delay)
  }
}

/**
 * 文件转base64字符串
 * @param {File} file 要转换的文件对象
 * @param {boolean} withMeta 是否包含dataURL前缀（默认true）
 * @returns {string} Promise返回base64字符串
 */
export const fileToBase64 = (
  file: File,
  withMeta = true
): Promise<string> => {
  return new Promise((resolve, reject) => {
    // 限制文件大小（示例限制为10MB）
    const MAX_SIZE = 10 * 1024 * 1024
    if (file.size > MAX_SIZE) {
      return reject(new Error('文件大小超过10MB限制'))
    }

    const reader = new FileReader()

    reader.onload = () => {
      let result = reader.result as string
      // 处理是否包含元数据前缀
      if (!withMeta) {
        result = result.split(',')[1]
      }
      resolve(result)
    }

    reader.onerror = error => reject(error)
    reader.readAsDataURL(file)
  })
}

/**
 * 是否有效值
 * @param {any} value - 要检查的值。
 * @returns {boolean} 返回是否有效值，包含0
 * @example isValidValue(0); // true
 * @example isValidValue('0'); // true
 * @example isValidValue(null); // false
 * @example isValidValue(undefined); // false
 * @example isValidValue(NaN); // false
 * @example isValidValue(''); // false
 * @example isValidValue('f57008a2e7b584cd0bbee3b3e3f67e47'); // true
 */
export const isValidValue = (value: any): value is boolean => {
  return value !== ''
    && value !== null
    && value !== undefined
    && !Number.isNaN(value)
}

/**
 * 缓存 Promise 的结果，以便在下一次调用时直接返回缓存的结果。
 * @param {function} fn 返回 Promise 的函数。
 * @returns {Promise} 返回一个新的函数，这个新函数会缓存第一次调用时返回的 Promise。
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function cachePromise<T, FnParams extends any[]>(fn: (...args: FnParams) => Promise<T>): (...args: FnParams) => Promise<T> {
  let promise: Promise<T> | null = null

  /**
   * 新函数。
   * @returns {Promise} 返回一个 Promise，这个 Promise 的结果是第一次调用时返回的 Promise 的结果。
   */
  const cachedFn = (...args: FnParams) => {
    if (promise === null) {
      promise = fn(...args)
      promise.then(() => {
        promise = null
      }).catch(() => {
        promise = null
      })
    }
    return promise
  }
  return cachedFn
}

/**
 * 缓存 Promise 的结果，以便在下一次调用时直接返回缓存的结果,不清空缓存结果只会调用一次。
 * @param {function} fn 返回 Promise 的函数。
 * @returns {Promise} 返回一个新的函数，这个新函数会缓存第一次调用时返回的 Promise。
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function cachePromiseNoClear<T, FnParams extends any[]>(fn: (...args: FnParams) => Promise<T>): (...args: FnParams) => Promise<T> {
  let promise: Promise<T> | null = null
  let result: T | null = null

  /**
   * 新函数。
   * @returns {Promise} 返回一个 Promise，这个 Promise 的结果是第一次调用时返回的 Promise 的结果。
   */
  const cachedFn = (...args: FnParams) => {
    if (result !== null) {
      // 已经有结果，直接返回一个 resolved 的 Promise
      return Promise.resolve(result)
    }

    if (promise === null) {
      // 没有正在进行的请求，执行并缓存 Promise
      promise = fn(...args)
        .then(res => {
          result = res // 缓存结果
          return res
        })
        .catch(err => {
          promise = null // 请求失败时允许重新请求
          throw err
        })
    }
    return promise
  }
  return cachedFn
}

/**
 * px转vw
 * @param {number} px - 像素值
 * @param {number} designWidth - 设计稿宽度，默认为375px
 * @returns {string} - 转换后的vw值
 */
export const pxToVw = (px: number, designWidth = 375): string => {
  return `${(px / designWidth) * 100}vw`
}

/**
 * 生成通知内容的函数
 * @param {number} count - 条数
 * @returns {string[]} - 通知内容数组列表
 */
export const generateNotices = (count = 10): string[] => {
  const notices: string[] = [];
  for (let i = 0; i < count; i++) {
    // 随机生成四位尾号
    const tailNumber = Math.floor(Math.random() * 10000).toString().padStart(4, '0');

    // 随机生成额度（5000-50000之间的整数）
    const amount = randomNum(5,50)

    // 格式化金额（添加千位分隔符）
    const formattedAmount = (amount * 1000).toLocaleString();

    // 生成通知内容
    const notice = `恭喜${tailNumber}用户成功放款${formattedAmount}元`;
    notices.push(notice);
  }
  return notices;
};

/**
 * 截取文本并在超出长度时添加省略号
 * @param {string} text - 原始文本
 * @param {number} maxLength - 最大长度
 * @returns {string} - 截取后的文本
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text) {
    return ''
  }
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

/**
 * 通过 iframe 打开安装包链接，保持在当页，失败时转为外部跳转方式
 * @param {string} url 安装包链接
 */
export const openInstallOnCurrentPage = (url: string) => {
  if (getSuffix(url) === 'apk') {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe)

    const removeIframe = () => iframe.remove();

    const timeoutId = setTimeout(() => {
      removeIframe();
      navigateToExternal(url)
    }, 800);

    iframe.onload = () => {
      clearTimeout(timeoutId);
      removeIframe();
    }

    iframe.onerror = () => {
      clearTimeout(timeoutId);
      removeIframe();
      navigateToExternal(url);
    }

    return
  } else {
    saveFile(url, '元信', 'apk')
  }
}


export const convertProductFormat:(product:RqResult) => RenderProductItem[] = (product: RqResult) => {
  const productList:RenderProductItem[] = []
  if (!product.id || !product.productName) return productList
  let isWholeProcess = product.thirdpartyType === ThirdpartyType.WholeProcess
  productList.push({
    id: product.id,
    productNickName: isWholeProcess ? product.yqlWholeProcessBumpVO?.partnerProductName ?? '' : product.productNickName ?? '',
    productLogo: isWholeProcess ? product.yqlWholeProcessBumpVO?.partnerLogo : product.productLogo ,
    productCompany: product.productCompany ?? '',
    wholeProcessProductCompany: isWholeProcess ? product.yqlWholeProcessBumpVO?.partnerCompanyName : '',
    thirdpartyType:product.thirdpartyType
  })
  if (product.mayAuthProductList && product.mayAuthProductList.length > 0) {
    product.mayAuthProductList.forEach(item => {
      let itemIsWholeProcess = item.thirdpartyType === ThirdpartyType.WholeProcess
      productList.push({
        id: item.id,
        productNickName: itemIsWholeProcess ? item.partnerProductName ?? '' : item.productNickName ?? '',
        productLogo: item.partnerLogo,
        productCompany: item.productCompany ?? '',
        wholeProcessProductCompany: isWholeProcess ? item.partnerCompanyName : '',
        thirdpartyType:item.thirdpartyType
      })
    })
  }
  return productList
}

/**
 * 封装 window.location.href ，方便后续处理
 * @param url 跳转链接
 * @param extra 3s未跳转，额外埋点参数
 * @param options 跳转配置
 */
interface NavigateToExternalOptions {
  openInNewTab?: boolean
  onNewTabResult?: (opened: boolean) => void
}

export const navigateToExternal = (
  url: string,
  extra?: Record<string, string | number | boolean | null>,
  options?: NavigateToExternalOptions
) => {
  trackWechatInstallApp(url)

  if (url && options?.openInNewTab) {
    const newTab = window.open(url, '_blank')
    if (newTab) {
      newTab.opener = null
      options.onNewTabResult?.(true)
      return true
    }
    options.onNewTabResult?.(false)
  }

  extra && trackDelayedJump({ url, ...extra })
  if (url) {
    window.location.href = url
  }
  return false
}
